import React, { useEffect, useState, useRef, createElement } from 'react';
// ─── Canvas drawing helpers ───────────────────────────────────────────────────
const COLORS = {
  bg: '#050505',
  blue: '#3B82F6',
  blueD: '#2563EB',
  blueDD: '#1D4ED8',
  blueL: '#60A5FA',
  blueLL: '#BFDBFE',
  white: '#FFFFFF',
  slate: '#94A3B8',
  slateDark: '#475569'
};
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
function drawWand2(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  ctx.save();
  const scale = size * 2 / 24;
  ctx.translate(cx - 12 * scale, cy - 12 * scale);
  ctx.scale(scale, scale);
  // Lucide Wand2 is STROKE-based: fill="none", stroke="currentColor"
  // stroke-width="2", stroke-linecap="round", stroke-linejoin="round"
  ctx.strokeStyle = '#D1D5DB';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = 'none';
  // All paths: wand body + sparkle lines — all stroked, none filled
  const paths = [
  // Wand body — closed path (stroked, not filled)
  'm21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z',
  // Sparkle lines
  'M14 7 l3 3', 'M5 6 v4', 'M19 14 v4', 'M10 2 v2', 'M7 8 H3', 'M21 16 h-4', 'M11 3 H9'];
  paths.forEach(d => {
    ctx.stroke(new Path2D(d));
  });
  ctx.restore();
}
// ─── LOGO 512×512 ────────────────────────────────────────
function drawLogoCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  // Background: bg-white/10 over dark ≈ #1a1a1f
  ctx.fillStyle = '#1a1a1f';
  roundRect(ctx, 0, 0, W, H, W * 0.188);
  ctx.fill();
  // Border: border-white/10
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 2;
  roundRect(ctx, 1, 1, W - 2, H - 2, W * 0.186);
  ctx.stroke();
  // Icon: 55.6% of container (w-5 in w-9)
  drawWand2(ctx, W / 2, H / 2, W * 0.278);
}
// ─── APPLE TOUCH ICON 180×180 ────────────────────────────
function drawAppleCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#1a1a1f';
  roundRect(ctx, 0, 0, W, H, W * 0.222);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, 1, 1, W - 2, H - 2, W * 0.214);
  ctx.stroke();
  drawWand2(ctx, W / 2, H / 2, W * 0.278);
}
// ─── FAVICON 32×32 ───────────────────────────────────────
function drawFaviconCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#1a1a1f';
  roundRect(ctx, 0, 0, W, H, W * 0.19);
  ctx.fill();
  drawWand2(ctx, W / 2, H / 2, W * 0.32);
}
// ─── OG image icon — also use wand
function drawOGCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  // Background
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, W, H);
  // Center glow
  const glow = ctx.createRadialGradient(W * 0.5, H * 0.42, 0, W * 0.5, H * 0.42, W * 0.55);
  glow.addColorStop(0, 'rgba(37,99,235,0.22)');
  glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  // Grid
  ctx.strokeStyle = 'rgba(255,255,255,0.025)';
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 60) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y < H; y += 60) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  // Corner accents
  const cSize = 50;
  ctx.strokeStyle = 'rgba(59,130,246,0.45)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, cSize);
  ctx.lineTo(0, 0);
  ctx.lineTo(cSize, 0);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(W - cSize, 0);
  ctx.lineTo(W, 0);
  ctx.lineTo(W, cSize);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, H - cSize);
  ctx.lineTo(0, H);
  ctx.lineTo(cSize, H);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(W - cSize, H);
  ctx.lineTo(W, H);
  ctx.lineTo(W, H - cSize);
  ctx.stroke();
  // Icon background circle
  const iconX = W / 2 - 210;
  const iconY = H / 2 - 28;
  const iconR = 68;
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(iconX, iconY, iconR + 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Wand2 icon in OG image
  drawWand2(ctx, iconX, iconY, iconR * 0.68);
  // Brand name
  const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textBaseline = 'middle';
  ctx.font = `bold 56px ${font}`;
  const textX = iconX + iconR + 28;
  ctx.fillStyle = COLORS.white;
  ctx.fillText('Magic ', textX, iconY - 2);
  const magicW = ctx.measureText('Magic ').width;
  ctx.fillStyle = COLORS.blue;
  ctx.fillText('AI', textX + magicW, iconY - 2);
  const aiW = ctx.measureText('AI').width;
  ctx.fillStyle = COLORS.white;
  ctx.fillText(' Builders', textX + magicW + aiW, iconY - 2);
  // Divider
  const divY = H / 2 + 30;
  const divGrad = ctx.createLinearGradient(W / 2 - 320, 0, W / 2 + 320, 0);
  divGrad.addColorStop(0, 'transparent');
  divGrad.addColorStop(0.3, COLORS.blueD);
  divGrad.addColorStop(0.7, COLORS.blueD);
  divGrad.addColorStop(1, 'transparent');
  ctx.strokeStyle = divGrad;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 320, divY);
  ctx.lineTo(W / 2 + 320, divY);
  ctx.stroke();
  // Headline
  ctx.fillStyle = '#F8FAFC';
  ctx.font = `500 37px ${font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('Best AI Website Builders — Rating & Comparison', W / 2, divY + 22);
  // Subline
  ctx.fillStyle = '#64748B';
  ctx.font = `400 21px ${font}`;
  ctx.fillText('Independent reviews · Hands-on testing · 9 languages', W / 2, divY + 74);
  // Badges
  const badges = ['Wix AI  4.9★', 'Framer AI  4.8★', 'Durable AI  4.7★', '+ 2 more'];
  const badgeY = divY + 124;
  const bw = 178;
  const bh = 36;
  const totalW = badges.length * bw + (badges.length - 1) * 12;
  let bx = W / 2 - totalW / 2;
  ctx.font = `500 14px ${font}`;
  badges.forEach(label => {
    ctx.fillStyle = 'rgba(37,99,235,0.1)';
    ctx.strokeStyle = 'rgba(59,130,246,0.35)';
    ctx.lineWidth = 1;
    roundRect(ctx, bx, badgeY, bw, bh, 18);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#93C5FD';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, bx + bw / 2, badgeY + bh / 2);
    bx += bw + 12;
  });
  // Domain
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.font = `400 14px ${font}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText('magicaibuilders.com', W - 36, H - 26);
}
// ─── Asset card component ─────────────────────────────────────────────────────
interface AssetCardProps {
  id: string;
  title: string;
  filename: string;
  size: string;
  canvasWidth: number;
  canvasHeight: number;
  previewScale?: number;
  drawFn: (canvas: HTMLCanvasElement) => void;
  note?: string;
}
function AssetCard({
  id,
  title,
  filename,
  size,
  canvasWidth,
  canvasHeight,
  previewScale = 1,
  drawFn,
  note
}: AssetCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [downloaded, setDownloaded] = useState(false);
  useEffect(() => {
    if (canvasRef.current) drawFn(canvasRef.current);
  }, [drawFn]);
  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 3000);
  };
  const previewW = Math.round(canvasWidth * previewScale);
  const previewH = Math.round(canvasHeight * previewScale);
  return <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col gap-4" data-id="element-544">
      <div data-id="element-545">
        <div className="flex items-center justify-between mb-1" data-id="element-546">
          <h2 className="text-sm font-semibold text-white/60 uppercase tracking-widest" data-id="element-547">
            {title}
          </h2>
          <span className="text-xs text-white/30 font-mono" data-id="element-548">{size}</span>
        </div>
        <p className="text-white font-mono text-sm" data-id="element-549">{filename}</p>
      </div>

      <div className="rounded-xl overflow-hidden flex items-center justify-center bg-black/40 border border-white/5" style={{
      minHeight: 160
    }} data-id="element-550">
        <canvas ref={canvasRef} id={id} width={canvasWidth} height={canvasHeight} style={{
        width: previewW,
        height: previewH,
        imageRendering: previewScale > 1 ? 'pixelated' : 'auto',
        display: 'block'
      }} data-id="element-551" />
      </div>

      {note && <p className="text-xs text-white/35 leading-relaxed" data-id="element-552">{note}</p>}

      <button onClick={handleDownload} className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${downloaded ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-accent-blue hover:bg-accent-hover text-white shadow-lg shadow-accent-blue/20'}`} data-id="element-553">
        {downloaded ? '✓ Downloaded — move to public/' : `⬇ Download ${filename}`}
      </button>
    </div>;
}
// ─── Main page ────────────────────────────────────────────────────────────────
export function AssetDownloadPage() {
  // Noindex — hide from search engines
  useEffect(() => {
    document.title = 'Asset Generator — Magic AI Builders';
    let meta = document.querySelector('meta[name="robots"]');
    if (meta) {
      meta.setAttribute('content', 'noindex, nofollow');
    } else {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(meta);
    }
    return () => {
      // Restore on unmount
      const m = document.querySelector('meta[name="robots"]');
      if (m) m.setAttribute('content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    };
  }, []);
  const assets: AssetCardProps[] = [{
    id: 'c-logo',
    title: 'Logo',
    filename: 'logo.png',
    size: '512 × 512 px',
    canvasWidth: 512,
    canvasHeight: 512,
    previewScale: 0.35,
    drawFn: drawLogoCanvas,
    note: 'Used in Organization schema (logo.png). Place in public/logo.png'
  }, {
    id: 'c-apple',
    title: 'Apple Touch Icon',
    filename: 'apple-touch-icon.png',
    size: '180 × 180 px',
    canvasWidth: 180,
    canvasHeight: 180,
    previewScale: 1,
    drawFn: drawAppleCanvas,
    note: 'Used by iOS when adding to home screen. Place in public/apple-touch-icon.png'
  }, {
    id: 'c-fav32',
    title: 'Favicon (32×32)',
    filename: 'favicon-32x32.png',
    size: '32 × 32 px',
    canvasWidth: 32,
    canvasHeight: 32,
    previewScale: 4,
    drawFn: drawFaviconCanvas,
    note: 'Download → convert to .ico at favicon.io/favicon-converter → save as public/favicon.ico'
  }, {
    id: 'c-fav16',
    title: 'Favicon (16×16)',
    filename: 'favicon-16x16.png',
    size: '16 × 16 px',
    canvasWidth: 16,
    canvasHeight: 16,
    previewScale: 8,
    drawFn: drawFaviconCanvas,
    note: 'Optional extra size. Also upload to favicon.io together with 32×32 for best .ico result.'
  }];
  return <div className="min-h-screen bg-[#050505] text-white" data-id="element-554">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#050505]/80 backdrop-blur-md sticky top-0 z-10" data-id="element-555">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-556">
          <div data-id="element-557">
            <h1 className="text-lg font-bold text-white" data-id="element-558">🪄 Asset Generator</h1>
            <p className="text-xs text-white/40 mt-0.5" data-id="element-559">
              Magic AI Builders — SEO Assets
            </p>
          </div>
          <span className="text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-3 py-1 rounded-full font-medium" data-id="element-560">
            noindex — hidden from Google
          </span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10" data-id="element-561">
        {/* Instructions */}
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6 mb-10" data-id="element-562">
          <h2 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wider" data-id="element-563">
            📋 How to use
          </h2>
          <ol className="text-sm text-white/60 space-y-1.5 list-decimal list-inside leading-relaxed" data-id="element-564">
            <li data-id="element-565">Download each file using the buttons below</li>
            <li data-id="element-566">
              Place all files in your project's{' '}
              <code className="text-white/80 bg-white/10 px-1.5 py-0.5 rounded text-xs" data-id="element-567">
                public/
              </code>{' '}
              folder with the exact filenames shown
            </li>
            <li data-id="element-568">
              For{' '}
              <code className="text-white/80 bg-white/10 px-1.5 py-0.5 rounded text-xs" data-id="element-569">
                favicon.ico
              </code>{' '}
              — download favicon-32x32.png, then convert at{' '}
              <a href="https://favicon.io/favicon-converter/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline" data-id="element-570">
                favicon.io/favicon-converter
              </a>
            </li>
            <li data-id="element-571">Download the OG image separately below (large canvas)</li>
          </ol>
        </div>

        {/* Icon assets grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5" data-id="element-572">
          {assets.map(asset => <AssetCard key={asset.id} {...asset} data-id="element-573" />)}
        </div>

        {/* OG Image — full width */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6" data-id="element-574">
          <div className="flex items-center justify-between mb-1" data-id="element-575">
            <h2 className="text-sm font-semibold text-white/60 uppercase tracking-widest" data-id="element-576">
              OG Image
            </h2>
            <span className="text-xs text-white/30 font-mono" data-id="element-577">
              1200 × 630 px
            </span>
          </div>
          <p className="text-white font-mono text-sm mb-4" data-id="element-578">og-image.png</p>

          <div className="rounded-xl overflow-hidden bg-black/40 border border-white/5 mb-4" data-id="element-579">
            <OGCanvasCard data-id="element-580" />
          </div>

          <p className="text-xs text-white/35 mb-4" data-id="element-581">
            Critical for Telegram/WhatsApp/Slack previews and Twitter Card.
            Place in public/og-image.png
          </p>
        </div>

        {/* Checklist */}
        <div className="mt-8 bg-white/[0.02] border border-white/8 rounded-2xl p-6" data-id="element-582">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4" data-id="element-583">
            ✅ Final Checklist — files to place in public/
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-id="element-584">
            {[{
            file: 'logo.svg',
            status: '✅',
            note: 'already exists (master vector)'
          }, {
            file: 'logo.png',
            status: '⬜',
            note: '512×512 — download above'
          }, {
            file: 'og-image.png',
            status: '⬜',
            note: '1200×630 — download above'
          }, {
            file: 'apple-touch-icon.png',
            status: '⬜',
            note: '180×180 — download above'
          }, {
            file: 'favicon.ico',
            status: '⬜',
            note: 'convert favicon-32x32.png at favicon.io'
          }, {
            file: 'favicon-32x32.png',
            status: '⬜',
            note: '32×32 — download above (optional)'
          }].map(({
            file,
            status,
            note
          }) => <div key={file} className="flex items-start gap-2 text-white/60" data-id="element-585">
                <span data-id="element-586">{status}</span>
                <span data-id="element-587">
                  <code className="text-white/80 text-xs bg-white/8 px-1.5 py-0.5 rounded" data-id="element-588">
                    {file}
                  </code>
                  <span className="text-white/35 ml-2 text-xs" data-id="element-589">{note}</span>
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// OG canvas needs separate component to handle its own ref + download
function OGCanvasCard() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [downloaded, setDownloaded] = useState(false);
  useEffect(() => {
    if (canvasRef.current) drawOGCanvas(canvasRef.current);
  }, []);
  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = 'og-image.png';
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 3000);
  };
  return <div className="p-4" data-id="element-590">
      <canvas ref={canvasRef} width={1200} height={630} style={{
      width: '100%',
      height: 'auto',
      display: 'block',
      borderRadius: 8
    }} data-id="element-591" />
      <button onClick={handleDownload} className={`mt-4 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${downloaded ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-accent-blue hover:bg-accent-hover text-white shadow-lg shadow-accent-blue/20'}`} data-id="element-592">
        {downloaded ? '✓ Downloaded — move to public/og-image.png' : '⬇ Download og-image.png'}
      </button>
    </div>;
}